import { type StyleObjectMulti } from '@frontend/legacy-components/src/theme/styleConfigHelpers';

import { TABLE_PARTS } from './tableParts';
import type { TablePart, TableSize, TableStyleConfig } from './types';

const HEADER_AND_FOOTER_BASE_STYLES = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  bg: 'page.surface',
  w: '100%',
  borderColor: 'separator.divider',
};

const BASE_STYLE = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    w: '100%',
    fontFamily: 'default',
    // Devlopers must override this to "unset" if using sticky header with RC-Table.
    overflow: 'hidden',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'separator.border',
    borderRadius: 'lg',
  },
  header: {
    ...HEADER_AND_FOOTER_BASE_STYLES,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
  },
  title: {
    fontFamily: 'default',
    fontWeight: 'heavy',
    m: 0,
  },
  footer: {
    ...HEADER_AND_FOOTER_BASE_STYLES,
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
  },
  htmlTable: {
    w: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  tr: {
    _notLast: {
      td: {
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'separator.divider',
      },
    },
  },
  thead: {
    tr: {
      th: {
        _last: {
          borderRightWidth: '0',
        },
      },
    },
  },
  th: {
    bg: 'page.plateLight',
    fontWeight: 'heavy',
    wordBreak: 'break-all',
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
    borderRightColor: 'separator.divider',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'separator.divider',
  },
  tbody: {},
  td: {
    _notLast: {
      borderRightWidth: '1px',
      borderRightStyle: 'solid',
      borderRightColor: 'separator.divider',
    },
  },
  tdInner: {
    bg: 'page.surface',
    fontWeight: 'default',
    wordBreak: 'break-all',
    textAlign: 'inherit',
    borderRadius: 'inherit',
    width: '100%',
    height: '100%',
    display: 'flex',
  },
  tfoot: {},
};

const getBaseSizeStyles = (size: TableSize): StyleObjectMulti<TablePart> => {
  const headerAndFooterStyles = {
    px: 1.5,
    minHeight: 5,
  };

  const fontStyles = {
    fontSize: size,
    lineHeight: size,
  };

  return {
    header: headerAndFooterStyles,
    footer: headerAndFooterStyles,
    td: fontStyles,
    th: fontStyles,
  };
};

const SIZES = {
  sm: () => {
    const base = getBaseSizeStyles('sm');

    return {
      ...base,
      header: { ...base.header, px: 1 },
      footer: { ...base.footer, px: 1 },
      title: { ...base.title, fontSize: 'md', lineHeight: 'md' },
      td: { ...base.td, h: 4 },
      th: { ...base.td, h: 4, px: 1, py: 0.5 },
      tdInner: { ...base.tdInner, px: 1, py: 0.5 },
      thead: {
        ...base.thead,
        tr: {
          ...base.thead?.tr,
          th: { ...base.th, h: 3, px: 1, py: 0.125 },
        },
      },
    };
  },
  md: () => {
    const base = getBaseSizeStyles('md');

    return {
      ...base,
      title: { ...base.title, fontSize: 'lg', lineHeight: 'lg' },
      td: { ...base.td, h: 5 },
      th: { ...base.td, h: 5, px: 1.5, py: 1 },
      tdInner: { ...base.tdInner, px: 1.5, py: 1 },
      thead: {
        ...base.thead,
        tr: {
          ...base.thead?.tr,
          th: { ...base.th, h: 4, px: 1.5, py: 0.25 },
        },
      },
    };
  },
};

const VARIANTS = {
  default: {},
  simple: {
    td: {
      borderRight: 'none !important',
    },
    th: {
      borderRight: 'none !important',
    },
  },
};

const DEFAULT_PROPS = {
  size: 'md',
  variant: 'default',
} as const;

export const TABLE_STYLE_CONFIG: TableStyleConfig = {
  parts: TABLE_PARTS,
  baseStyle: BASE_STYLE,
  sizes: SIZES,
  variants: VARIANTS,
  defaultProps: DEFAULT_PROPS,
};
